import React from 'react';
import eventsApi, { EventAndOwner } from 'services/eventsApi';
import { getCurrentBlockOutDates, hasCurrentBlockOutDates, renderCustomizeLookPath } from '../utils/utils';
import { BlockoutDate, Item } from 'types';
import * as dateFns from 'date-fns';

import FormRadioItem from 'components/FormRadioItem';

const { format, addDays } = dateFns;

interface BundleRadioOptionsProps {
  product: Item;
  radioButtonSelection: string;
  handleRadioChange: (option: string, itemId: number) => void;
}

const BundleRadioOptions = ({ product, radioButtonSelection, handleRadioChange }: BundleRadioOptionsProps) => {
  const isSelected = (value: string) => radioButtonSelection === value;

  return (
    <div className="space-y-8 sm:space-y-8">
      <div className="flex items-end justify-between">
        <div className="relative inline-block text-left">
          <FormRadioItem
            className="!mx-0 -mb-8 !gap-8"
            backgroundClassName="bg-transparent"
            label="Rent"
            checked={isSelected('Rent')}
            onChange={() => handleRadioChange('Rent', product.id)}
          />
        </div>

        <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

        <p className={`text-h4 leading-3 ${isSelected('Rent') ? 'text-black' : '!text-gray line-through'} ml-8`}>${product.cost}</p>
      </div>

      <div className="flex items-end justify-between">
        <div className="relative inline-block text-left">
          <FormRadioItem
            className="!mx-0 -mb-8 !gap-8"
            backgroundClassName="bg-transparent"
            label="Buy"
            checked={isSelected('Buy')}
            onChange={() => handleRadioChange('Buy', product.id)}
          />
        </div>

        <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

        <p className={`text-h4 leading-3 ${isSelected('Buy') ? 'text-black' : '!text-gray line-through'} ml-8`}>
          ${product.retail_bundle!.cost}
        </p>
      </div>
    </div>
  );
};

interface CustomizeButtonProps {
  product: Item;
  radioButtonSelection: string;
  builderQuery: string;
  isBlockedOut: boolean;
  isComingSoonBlockedOut: boolean;
}

const CustomizeButton = ({ product, radioButtonSelection, builderQuery, isBlockedOut, isComingSoonBlockedOut }: CustomizeButtonProps) => {
  const getCustomizeBtnText = (product: Item) => {
    if (product.is_retail) {
      return 'Customize Purchase';
    }

    const productHasRetailBundle = (product: Item) => product.retail_bundle?.displayable && product.retail_bundle?.is_active;

    const isRetail = productHasRetailBundle(product) ? radioButtonSelection === 'Buy' : product.type === 'Purchase';

    return `Customize ${isRetail ? 'Purchase' : 'Rental'}`;
  };

  const getBuilderQuery = () => {
    const isBundle = product.category!.toLowerCase() === 'preconfigured';

    const param = builderQuery.split('=')[0];

    if (!isBundle) {
      return `${param}=${product.sku}`;
    }

    if (radioButtonSelection === 'Rent') {
      return `${param}=${product.id}`;
    }

    return `${param}=${product.retail_bundle?.id!}`;
  };

  const getFirstAvailableDate = (blockoutDates: BlockoutDate[]) => {
    const currentDates = getCurrentBlockOutDates(blockoutDates);

    const date = currentDates.filter((date) => date.availability === 'coming-soon').shift();

    const endDate = date?.endDate ?? date?.end_date;

    if (!endDate) {
      throw new Error('Called getFirstAvailableDate() without any qualifying blockout dates');
    }

    const availableDate = addDays(
      new Date(endDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York',
      }),
      2,
    );

    return format(availableDate, 'MMM. Do, yyyy');
  };

  const isNormalBlock = isBlockedOut && !isComingSoonBlockedOut;
  const isComingSoonBlock = isComingSoonBlockedOut;

  return (
    <div className="space-y-8">
      <button
        className="btn-info tracker-cta-product_page-hero_customize-20190208-091145 btn relative z-10 w-full"
        disabled={isBlockedOut || isComingSoonBlockedOut}
        onClick={() =>
          !isBlockedOut && !isComingSoonBlockedOut && (window.location.href = `${renderCustomizeLookPath()}${getBuilderQuery()}`)
        }
      >
        {getCustomizeBtnText(product)}
      </button>

      {isNormalBlock && (
        <p className="text-sm">
          Customize disabled for limited items.{' '}
          <a href={`${process.env.NEXT_PUBLIC_APP_URL}/signup/email`} className="tracker-cta-product_page-hero_sign_up-200623-093050">
            Create an event
          </a>{' '}
          to see if this item available for your date.
        </p>
      )}

      {isComingSoonBlockedOut && (
        <p className="mt-8 text-sm">
          Available {getFirstAvailableDate(product.blockoutDates ?? product.blockout_dates ?? [])}, but you can keep planning! Build your
          look,{' '}
          <a
            href={`${process.env.NEXT_PUBLIC_APP_URL}/signup/email`}
            className="tracker-cta-product_page-hero_sign_up-200623-093050 inline-block"
          >
            create an event
          </a>
          , & order swatches today.
        </p>
      )}
    </div>
  );
};

interface CustomizeBoxProps {
  isBlockedOut: boolean;
  isComingSoonBlockedOut: boolean;
  builderQueryString: string;
  product: Item;
  showHtoButton?: boolean;
  htoUrl?: string;
}

interface CustomizeBoxState {
  radioButtonSelection: string;
  isSelectionBlockedOut: boolean;
  primaryEvent: EventAndOwner | null;
}

export default class CustomizeBox extends React.Component<CustomizeBoxProps, CustomizeBoxState> {
  state: CustomizeBoxState = {
    radioButtonSelection: 'Rent',
    isSelectionBlockedOut: this.props.isBlockedOut || this.props.isComingSoonBlockedOut,
    primaryEvent: null,
  };

  componentDidMount() {
    eventsApi.fetchEventsAndPrimaryEventId().then(({ events, primaryEventId }) => {
      this.setState({
        primaryEvent: events.find((e) => e.id === primaryEventId) ?? null,
      });
    });
  }

  isSelectionBlockedOut = (val: string) => {
    if (window.location.href.includes('collection') && this.props.product.retail_bundle && val === 'Buy') {
      // recheck blockoutdate with updated selection
      const blockoutDates = this.props.product.retail_bundle?.blockout_dates ?? [];
      const nonHTOBlockouts = blockoutDates.filter((x) => x.availability !== 'hto');

      return hasCurrentBlockOutDates(nonHTOBlockouts, this.state.primaryEvent?.startDate);
    }

    return this.props.isBlockedOut || this.props.isComingSoonBlockedOut;
  };

  handleRadioChange = (val: string) => {
    this.setState({
      radioButtonSelection: val,
      isSelectionBlockedOut: this.isSelectionBlockedOut(val),
    });
  };

  render() {
    const { product } = this.props;
    const { radioButtonSelection } = this.state;
    const canCustomizeProduct = product.displayable && product.is_active;
    const isBundle = product.category!.toLowerCase() === 'preconfigured';

    return (
      <div className="space-y-16 md:space-y-32">
        {canCustomizeProduct ? (
          <>
            {!!product.retail_bundle && product.retail_bundle.displayable && product.retail_bundle.is_active ? (
              <BundleRadioOptions
                product={product}
                radioButtonSelection={radioButtonSelection}
                handleRadioChange={this.handleRadioChange}
              />
            ) : (
              <div className="flex items-end justify-between">
                <p className="mr-8 leading-3">{product.type === 'Purchase' || product.is_retail ? 'Buy' : 'Rent'}</p>

                <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

                <p className="text-h4 ml-8 leading-3">${product.cost}</p>
              </div>
            )}

            <CustomizeButton
              product={product}
              radioButtonSelection={radioButtonSelection}
              builderQuery={this.props.builderQueryString}
              isBlockedOut={this.props.isBlockedOut}
              isComingSoonBlockedOut={this.props.isComingSoonBlockedOut}
            />

            {this.props.showHtoButton && isBundle && (
              <a
                href={this.props.htoUrl}
                role="button"
                tabIndex={1}
                className="tracker-cta-product_page-hero_free-hto-20250408-122803 btn-info btn relative z-10 w-full sm:hidden"
              >
                Free Home Try-On
              </a>
            )}
          </>
        ) : (
          <p>Product No Longer Available</p>
        )}
      </div>
    );
  }
}
